/**
 * ResizableImage — расширение поверх `@tiptap/extension-image`, добавляющее
 * возможность менять ширину картинки перетаскиванием за уголок.
 *
 * Хранение размера. Контент полей хранится как Markdown (см. tiptap-markdown),
 * а стандартный `![alt](url)` не умеет нести размер. Чтобы ширина пережила
 * round-trip Markdown ↔ редактор ↔ просмотр (react-markdown), не включая
 * сырой HTML, ширина кодируется в query-параметр URL: `![alt](url?w=300)`.
 * Оба сериализатора (tiptap-markdown и remark) переносят строку URL как есть,
 * а сервер вложений query-параметр игнорирует (отдаёт файл по route-id).
 *
 * В самой ProseMirror-модели ширина живёт отдельным атрибутом `width` (px),
 * а `src` остаётся чистым (без `?w=`); query собирается только на сериализации
 * и разбирается на парсинге — единый источник правды это атрибут `width`.
 */
import Image from '@tiptap/extension-image';
import type { NodeViewRendererProps } from '@tiptap/react';

const WIDTH_QUERY = /[?&]w=(\d+)\b/;
/** Минимальная ширина при ресайзе, px. */
const MIN_WIDTH = 40;

/** Разбирает `url?w=300` → `{ src: 'url', width: 300 }`. Для чистого URL width = null. */
export function parseWidthFromSrc(rawSrc: string): { src: string; width: number | null } {
  const m = rawSrc.match(WIDTH_QUERY);
  if (!m) return { src: rawSrc, width: null };
  const width = Number.parseInt(m[1] ?? '', 10);
  // Убираем именно параметр w, сохраняя остальной query при его наличии.
  let src = rawSrc.replace(/([?&])w=\d+&?/, (_full, sep: string) => (sep === '?' ? '?' : '&'));
  src = src.replace(/[?&]$/, '');
  return { src, width: Number.isFinite(width) ? width : null };
}

/** Собирает `url` + `?w=300`. Если width пуст — возвращает url без изменений. */
export function appendWidth(src: string, width: number | null | undefined): string {
  if (!width) return src;
  const sep = src.includes('?') ? '&' : '?';
  return `${src}${sep}w=${width}`;
}

export const ResizableImage = Image.extend({
  addAttributes() {
    const parent: Record<string, unknown> = this.parent?.() ?? {};
    const parentSrc = (parent.src ?? {}) as Record<string, unknown>;
    return {
      ...parent,
      // src чистим от ?w=, чтобы width был единственным источником правды.
      src: {
        ...parentSrc,
        parseHTML: (element: HTMLElement) => parseWidthFromSrc(element.getAttribute('src') ?? '').src,
      },
      width: {
        default: null,
        parseHTML: (element: HTMLElement): number | null => {
          const attrW = element.getAttribute('width');
          if (attrW && /^\d+$/.test(attrW)) return Number.parseInt(attrW, 10);
          const styleW = element.style?.width;
          if (styleW && styleW.endsWith('px')) return Number.parseInt(styleW, 10) || null;
          return parseWidthFromSrc(element.getAttribute('src') ?? '').width;
        },
        renderHTML: (attrs: { width?: number | null }) => {
          if (!attrs.width) return {};
          return { width: attrs.width, style: `width: ${attrs.width}px` };
        },
      },
    };
  },

  addStorage() {
    const parent = this.parent?.() ?? {};
    return {
      ...parent,
      // Сериализация в Markdown с кодированием ширины в ?w=. Формат совпадает
      // с дефолтным (prosemirror-markdown), плюс query-параметр ширины.
      markdown: {
        serialize(
          state: { write: (s: string) => void; esc: (s: string) => string },
          node: { attrs: { src: string; alt?: string; title?: string; width?: number | null } },
        ) {
          const src = appendWidth(node.attrs.src, node.attrs.width).replace(/[()]/g, '\\$&');
          const alt = state.esc(node.attrs.alt ?? '');
          const title = node.attrs.title ? ` "${node.attrs.title.replace(/"/g, '\\"')}"` : '';
          state.write(`![${alt}](${src}${title})`);
        },
        parse: {}, // markdown → HTML делает markdown-it; width вытащит parseHTML атрибута
      },
    };
  },

  addNodeView() {
    return (props: NodeViewRendererProps) => {
      const { editor, getPos } = props;
      let node = props.node;

      const container = document.createElement('div');
      container.className = 'lens-img-resizable';

      const img = document.createElement('img');
      img.className = (node.attrs.class as string) || 'lens-md-img';
      img.draggable = false;

      const handle = document.createElement('span');
      handle.className = 'lens-img-resize-handle';
      handle.title = 'Потянуть, чтобы изменить размер';

      container.appendChild(img);
      container.appendChild(handle);

      const applyAttrs = (n: typeof node): void => {
        img.src = appendWidth(n.attrs.src as string, n.attrs.width as number | null);
        if (n.attrs.alt) img.alt = n.attrs.alt as string;
        else img.removeAttribute('alt');
        if (n.attrs.width) img.style.width = `${n.attrs.width as number}px`;
        else img.style.removeProperty('width');
      };
      applyAttrs(node);

      let dragging = false;
      let startX = 0;
      let startW = 0;

      const onMove = (e: PointerEvent): void => {
        if (!dragging) return;
        const next = Math.max(MIN_WIDTH, Math.round(startW + (e.clientX - startX)));
        img.style.width = `${next}px`;
      };

      const onUp = (): void => {
        if (!dragging) return;
        dragging = false;
        container.classList.remove('is-resizing');
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        const width = Number.parseInt(img.style.width, 10) || null;
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        if (pos == null) return;
        const tr = editor.view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, width });
        editor.view.dispatch(tr);
      };

      handle.addEventListener('pointerdown', (e) => {
        if (!editor.isEditable) return;
        e.preventDefault();
        e.stopPropagation();
        dragging = true;
        startX = e.clientX;
        startW = img.getBoundingClientRect().width;
        container.classList.add('is-resizing');
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
      });

      return {
        dom: container,
        update: (updated) => {
          if (updated.type.name !== node.type.name) return false;
          node = updated;
          if (!dragging) applyAttrs(updated);
          return true;
        },
        selectNode: () => container.classList.add('is-selected'),
        deselectNode: () => container.classList.remove('is-selected'),
        ignoreMutation: () => true,
        destroy: () => {
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
        },
      };
    };
  },
});
